import { createClient } from '@supabase/supabase-js';

const supabaseUrl  = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey  = import.meta.env.VITE_SUPABASE_ANON_KEY;
const SUPABASE_FETCH_CONCURRENCY = 6;

const supabaseFetchQueue = [];
let supabaseFetchActive = 0;

function runSupabaseFetchQueue() {
  while (supabaseFetchActive < SUPABASE_FETCH_CONCURRENCY && supabaseFetchQueue.length) {
    const next = supabaseFetchQueue.shift();
    supabaseFetchActive += 1;
    next.job()
      .then(next.resolve, next.reject)
      .finally(() => {
        supabaseFetchActive -= 1;
        runSupabaseFetchQueue();
      });
  }
}

function enqueueSupabaseFetch(job) {
  return new Promise((resolve, reject) => {
    supabaseFetchQueue.push({ job, resolve, reject });
    runSupabaseFetchQueue();
  });
}

function limitedSupabaseFetch(url, options) {
  return enqueueSupabaseFetch(() => fetch(url, options));
}

if (!supabaseUrl || !supabaseKey) {
  console.warn(
    '[SHADOWLAND] Supabase env vars missing. ' +
    'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file. ' +
    'Falling back to localStorage store.'
  );
}

export const supabase = (supabaseUrl && supabaseKey)
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        storageKey: 'shadowland-auth',
        flowType: 'pkce',
        autoRefreshToken: true,
        // OAuth code exchange is handled explicitly by AuthCallback.
        // Leaving auto-detection on lets the root route and Supabase
        // client race each other, which can bounce users back to login.
        detectSessionInUrl: false,
        // Skip Navigator Lock API to prevent "lock not released" errors
        // caused by React Strict Mode double-mounting
        lock: async (name, acquireTimeout, fn) => fn(),
      },
      db: {
        schema: 'public',
      },
      global: {
        headers: { 'x-connection-pool': 'true' },
        // Custom fetch wrapper. We deliberately do NOT set
        // `keepalive: true` globally: the Fetch spec caps keepalive
        // request bodies at 64 KB, and Chrome silently hangs
        // larger requests instead of erroring. That broke storage
        // uploads >64 KB (PDFs, flowcharts) while leaving all the
        // smaller REST/auth calls working — a confusing partial
        // failure. We do queue Supabase calls so the app's startup
        // data hydrate cannot exhaust Chrome's available request slots
        // while map and ArcGIS requests are also active.
        fetch: limitedSupabaseFetch,
      },
    })
  : null;

export const isSupabaseEnabled = Boolean(supabase);
